import { useContext, useEffect, useMemo, useRef } from 'react';
import { PushContext } from '@hooks/usePush/PushContext.tsx';
import { JobEventsContext, JobItemEvent, JobSnapshot, isTerminalJobStatus } from './JobEventsContext.tsx';

/** Live snapshots of the user's jobs, active first — data source for the (future) jobs list UI. */
export const useActiveJobs = (): JobSnapshot[] => {
  const { jobs } = useContext(JobEventsContext);
  return useMemo(() => {
    const all = [...jobs.values()];
    const active = all.filter(s => !isTerminalJobStatus(s.status)).sort((a, b) => b.updatedAt - a.updatedAt);
    const terminal = all.filter(s => isTerminalJobStatus(s.status)).sort((a, b) => b.updatedAt - a.updatedAt);
    return [...active, ...terminal];
  }, [jobs]);
};

/** One job's live counters/status — header/progress-bar data with zero REST. */
export const useJobProgress = (jobId: string | undefined): JobSnapshot | undefined => {
  const { jobs } = useContext(JobEventsContext);
  return jobId != null ? jobs.get(jobId) : undefined;
};

interface UseJobEventsOptions {
  /** Per-item payload from job.progress events — patch local rows with it. */
  onItem?: (item: JobItemEvent) => void;
  /**
   * Fires ONCE when the snapshot is first observed in a terminal status (DONE/ERROR/CANCELLED) —
   * the place for the final reconciliation GET. Triggered whether the terminal state arrived via
   * push or via the provider's degraded-mode reseed, so it cannot be missed. NOTE: it also fires
   * for jobs that were already finished before the page opened — guard the refetch by checking
   * whether the locally fetched detail is already terminal.
   */
  onFinished?: (snapshot: JobSnapshot) => void;
  /** Push connection recovered from a drop — refetch, hints may have been lost. */
  onReconnect?: () => void;
}

export const useJobEvents = (jobId: string | undefined, options: UseJobEventsOptions) => {
  const { addItemListener } = useContext(JobEventsContext);
  const { reconnectCount } = useContext(PushContext);
  const snapshot = useJobProgress(jobId);

  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    if (jobId == null) return;
    return addItemListener(jobId, item => optionsRef.current.onItem?.(item));
  }, [jobId]);

  // onFinished on the transition into a terminal status, once per job. The ref records WHICH job it
  // fired for rather than a boolean reset by a second effect: a plain flag plus a reset effect is
  // re-armed by React 18's mount/cleanup/mount in development, which fires the callback twice.
  const finishedFiredForRef = useRef<string | null>(null);
  useEffect(() => {
    if (snapshot == null || !isTerminalJobStatus(snapshot.status) || finishedFiredForRef.current === jobId) return;
    finishedFiredForRef.current = jobId ?? null;
    optionsRef.current.onFinished?.(snapshot);
  }, [jobId, snapshot?.status]);

  // Remember the count already seen rather than "is this the first run": the boolean form is re-armed
  // by the development remount and reports a reconnect that never happened.
  const seenReconnectRef = useRef<number | null>(null);
  useEffect(() => {
    if (seenReconnectRef.current === reconnectCount) return;
    const firstRun = seenReconnectRef.current === null;
    seenReconnectRef.current = reconnectCount;
    if (!firstRun) optionsRef.current.onReconnect?.();
  }, [reconnectCount]);
};
